import Vue from 'vue';
import { v4 as uuid4 } from 'uuid';
import UploadedFileModel, { UploadedFileData, create } from "../models/UploadedFile";
import { Commit} from "vuex";
import ApiService from "../lib/api";

interface FileState {
    files: { [propName: string]: UploadedFileModel }
}

interface FileError {
    reference: string;
    message: string;
}

interface FileActions {
    commit: Commit;
}

const state: FileState = {
    files: {}
};

const mutations = {
    newFile(store: FileState, payload: UploadedFileModel): void {
        if (payload.data.reference) {
            Vue.set(store.files, payload.data.reference, payload);
        }
    },
    setProgress(store: FileState, payload: { reference: string, progress: number }): void {
        store.files[payload.reference].progress = payload.progress;
    },
    deleteFile(store: FileState, reference: string): void {
        if (store.files[reference]) {
            Vue.delete(store.files, reference);
        }
    },
    update(store: FileState, payload: UploadedFileModel): void {
        const ref = payload.data.reference;
        if (ref && store.files[ref]) {
            for (const i in payload.data) {
                if (i === 'reference') continue;
                Vue.set(store.files[ref].data, i, payload.data[i]);
            }
        }
    },
    setError(store: FileState, payload: FileError): void {
        if (payload.reference && store.files[payload.reference]) {
            Vue.set(store.files[payload.reference].errors, 'message', payload.message);
        }
    },
    setChunk(store: FileState, payload: {reference: string, chunk: number}): void {
        const file = store.files[payload.reference];
        Vue.set(file, 'previousChunk', file.currentChunk);
        Vue.set(file, 'currentChunk', payload.chunk);
    }
};

const actions = {
    create({commit}: FileActions, payload: UploadedFileModel): Promise<any>  {
        return new Promise((resolve => {
            payload.data.reference = uuid4();
            payload.progress = 0;
            commit('newFile', payload);
            resolve(payload);
        }));
    },
    addExisting({commit}: FileActions, payload: UploadedFileModel): void {
        commit('newFile', payload);
    },
    setProgress({commit}: FileActions, payload: { reference: string, progress: number }): void {
        commit('setProgress', payload);
    },
    setCurrentChunk({commit}: FileActions, payload: {reference: string, chunk: number}): void {
        commit('setChunk', payload);
    },
    deleteFile({commit}: FileActions, ref: string): Promise<any> {
        return new Promise((resolve, reject) => {
           ApiService.delete('/files/' + ref).then(() => {
               commit('deleteFile', ref);
               resolve();
           }).catch(reject);
        });
    },
    update({commit}: FileActions, payload: UploadedFileModel): Promise<any>  {
        commit('update', payload);
        return new Promise(((resolve, reject) => {
            ApiService.patch('/files/' + payload.data.reference, payload.data).then((resp: any) => {
                resolve();
            }).catch(() => {
                alert('Failed to update file.');
            });
        }));
    },
    setError({commit}: FileActions, payload: FileError): void {
        commit('setError', payload);
    },
    syncFromServer({commit}: FileActions, ref: string): Promise<any> {
        return new Promise((resolve, reject) => {
           ApiService.get('/files/' + ref).then((resp: ApiResponseSingle<UploadedFileData>) => {
               commit('update', create(resp.data));
              resolve();
           }).catch(reject);
        });
    }
};

const getters = {
    filesByRefs: (state: FileState) => (refs: string[]) => {
        const files = [];
        for (const i in state.files) {
            if (refs.indexOf(i) > -1) {
                files.push(state.files[i]);
            }
        }
        return files;
    },
    fileByRef: (state: FileState) => (ref: string) => {
        for (const i in state.files) {
            if (ref === i) {
                return state.files[i];
            }
        }
        return null;
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
